/* eslint-disable react/function-component-definition */
import { Query } from "@apollo/client/react/components";
import OverdueSubscriptionStickyBanner from "components/billing/OverdueSubscriptionStickyBanner";
import BrazilianCompanyContext from "components/company/BrazilianCompanyContext";
import BrazilianCompanyPage from "components/company/v2/BrazilianCompanyPage";
import LocaleContext from "components/i18n/LocaleContext";
import WholeIssuerPageSkeleton from "components/issuers/WholeIssuerPageSkeleton";
import NavbarLayout from "components/layouts/NavbarLayout";
import hoursToSeconds from "date-fns/hoursToSeconds";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import Redirect from "lib/http/Redirect";
import SetHttpStatusCode from "lib/http/SetHttpStatusCode";
import IssuerByTickerSymbolQuery from "lib/queries/IssuerByTickerSymbolQuery";
import type {
  IIssuerByTickerSymbolQuery,
  IIssuerByTickerSymbolQueryVariables,
} from "lib/queries/__generated__/IssuerByTickerSymbolQuery.generated";
import getBaseUrl from "lib/utils/getBaseUrl";
import type { NextPage } from "next";
import NotFound from "pages/404";
import { ISecurityCountry } from "types/graphqlTypes";

interface BrazilianIssuerPageProps {
  tickerSymbol: string | string[] | undefined;
}

const BrazilianIssuer: NextPage<BrazilianIssuerPageProps> = ({ tickerSymbol }) => {
  const normalizedTickerSymbol = Array.isArray(tickerSymbol)
    ? tickerSymbol[0].toUpperCase()
    : (tickerSymbol || "").toUpperCase();

  if (!normalizedTickerSymbol || normalizedTickerSymbol.length > 8) {
    return (
      <>
        <SetHttpStatusCode httpStatus={404} />
        <NotFound />
      </>
    );
  }

  if (/^([0-9]+)$/.test(normalizedTickerSymbol)) {
    return (
      <>
        <SetHttpStatusCode httpStatus={404} />
        <NotFound />
      </>
    );
  }

  return (
    <Query<IIssuerByTickerSymbolQuery, IIssuerByTickerSymbolQueryVariables>
      query={IssuerByTickerSymbolQuery}
      variables={{
        tickerSymbol: normalizedTickerSymbol,
        country: ISecurityCountry.Br,
      }}
    >
      {({ data: currentData, loading, previousData }) => {
        const data = loading && previousData ? previousData : currentData;

        if (!data && loading) {
          return (
            <NavbarLayout>
              <WholeIssuerPageSkeleton />
            </NavbarLayout>
          );
        }

        if (!data || !data.issuerByTickerSymbol) {
          return (
            <>
              <SetHttpStatusCode httpStatus={404} />
              <NotFound />
            </>
          );
        }

        if (data.issuerByTickerSymbol.__typename === "BrazilianRealEstateFund") {
          return (
            <Redirect
              httpStatus={301}
              to={`${getBaseUrl()}/fiis/${data.issuerByTickerSymbol.tickerSymbol}`.toLowerCase()}
            />
          );
        }

        if (data.issuerByTickerSymbol.__typename === "BrazilianCompanyIssuer") {
          const security = getMatchedSecurity(data.issuerByTickerSymbol, normalizedTickerSymbol);
          if (!security) {
            return (
              <>
                <SetHttpStatusCode httpStatus={404} />
                <NotFound />
              </>
            );
          }
          // Redirect legacy symbols
          if (normalizedTickerSymbol.length === 4 && security) {
            return <Redirect httpStatus={301} to={`${getBaseUrl()}/br/${security.tickerSymbol}`.toLowerCase()} />;
          }
          return (
            <NavbarLayout>
              <OverdueSubscriptionStickyBanner />

              <LocaleContext.Provider value="BRA">
                <BrazilianCompanyContext.Provider value={data.issuerByTickerSymbol}>
                  <BrazilianCompanyPage
                    issuer={data.issuerByTickerSymbol}
                    tickerSymbol={normalizedTickerSymbol}
                    security={security}
                  />
                </BrazilianCompanyContext.Provider>
              </LocaleContext.Provider>
            </NavbarLayout>
          );
        }

        return (
          <>
            <SetHttpStatusCode httpStatus={404} />
            <NotFound />
          </>
        );
      }}
    </Query>
  );
};

BrazilianIssuer.getInitialProps = async ({ query, req, res }) => {
  // To prevent this from running on the client-side
  if (res) {
    const hasToken = req?.headers.cookie && parseRawCookies(req.headers.cookie).has("token");
    if (!hasToken) {
      res.setHeader("cache-control", `public, s-maxage=${hoursToSeconds(4)}`);
    }
  }
  return {
    tickerSymbol: Array.isArray(query.tickerSymbol) ? query.tickerSymbol[0] : query.tickerSymbol,
  };
};

export default BrazilianIssuer;

function getMatchedSecurity(issuer: IBrazilianCompanyIssuerTopLevelFragment, forTickerSymbolQuery: string) {
  if (issuer.__typename !== "BrazilianCompanyIssuer") {
    return null;
  }
  const matchedSecurity = issuer.securities.find((candidate) => {
    return candidate.tickerSymbol === forTickerSymbolQuery;
  });
  if (matchedSecurity) {
    return matchedSecurity;
  }
  const topSecurities = [...issuer.securities].sort((a, b) => {
    return b.sharesOutstanding - a.sharesOutstanding;
  });
  if (topSecurities.length > 0) {
    return topSecurities[0];
  }
  return null;
}

function parseRawCookies(cookies: string) {
  return cookies.split(";").reduce<Map<string, any>>((parsed, cookie) => {
    const [key, value] = cookie.trim().split("=");
    try {
      return parsed.set(key, JSON.parse(value));
    } catch {
      return parsed.set(key, value);
    }
  }, new Map());
}
